import moment from 'moment';
import { FilterDefinition } from './list-layout.types';

export function formatDate(date: Date | string | null | undefined, format = 'DD.MM.YYYY'): string {
    if (!date) {
        return '';
    }
    return moment(date).format(format);
}

export function formatDateTime(date: Date | string | null | undefined, format = 'DD.MM.YYYY HH:mm'): string {
    if (!date) {
        return '';
    }
    return moment(date).format(format);
}

export function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    return function (this: any, ...args: Parameters<T>) {
        const context = this;
        if (timeout !== null) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => func.apply(context, args), wait);
    };
}

export function generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function isValidDate(date: any): boolean {
    if (!date) {
        return false;
    }
    return moment(date).isValid();
}

export function buildQueryFromFilters(filters: Record<string, any>, filterDefinitions: FilterDefinition[]): any {
    const query: any = {};

    Object.keys(filters).forEach((key) => {
        const value = filters[key];
        const definition = filterDefinitions.find((f) => f.property === key);

        if (value === null || value === undefined || value === '') {
            return;
        }

        if (!definition) {
            query[key] = value;
            return;
        }

        switch (definition.type) {
            case 'text':
                if (typeof value === 'string' && value.trim() !== '') {
                    query[key] = { contains: value.trim() };
                }
                break;
            case 'select':
                query[key] = value;
                break;
            case 'multiselect':
                if (Array.isArray(value) && value.length > 0) {
                    query[key] = { in: value };
                }
                break;
            case 'date':
                if (isValidDate(value)) {
                    query[key] = moment(value).startOf('day').toISOString();
                }
                break;
            case 'daterange':
                if (value.from && isValidDate(value.from)) {
                    query[`${key}From`] = moment(value.from).startOf('day').toISOString();
                }
                if (value.to && isValidDate(value.to)) {
                    query[`${key}To`] = moment(value.to).endOf('day').toISOString();
                }
                break;
            case 'number': {
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                    query[key] = numValue;
                }
                break;
            }
            case 'boolean':
                query[key] = Boolean(value);
                break;
            default:
                query[key] = value;
        }
    });

    return query;
}

export function getNestedProperty(obj: any, path: string): any {
    if (!obj || !path) {
        return undefined;
    }

    const keys = path.split('.');
    let result = obj;

    for (const key of keys) {
        if (result === null || result === undefined) {
            return undefined;
        }
        result = result[key];
    }

    return result;
}

export function downloadFile(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export function calculatePagination(total: number, skip: number, take: number) {
    const currentPage = Math.floor(skip / take) + 1;
    const totalPages = Math.ceil(total / take);

    return {
        total,
        skip,
        take,
        currentPage,
        totalPages,
    };
}

export function cloneDeep<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (obj instanceof Date) {
        return new Date(obj.getTime()) as any;
    }

    if (Array.isArray(obj)) {
        return obj.map((item) => cloneDeep(item)) as any;
    }

    const clonedObj: any = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            clonedObj[key] = cloneDeep(obj[key]);
        }
    }

    return clonedObj;
}
